import React, { forwardRef } from "react";

import { FlipProp, IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { twJoin } from "tailwind-merge";

import { getSize } from "src/design-system/utils/sizes";

export type IconSize = "3xs" | "2xs" | "xs" | "sm";

export type IconVariant =
  | "default"
  | "disabled"
  | "active"
  | "hover"
  | "error"
  | "brand";

export type IconProps = {
  icon: IconProp;
  size?: IconSize;
  variant?: IconVariant;
  dataLoc?: string;
  spin?: boolean;
  flip?: FlipProp;
};

export type IconButtonProps = {
  icon: IconProp;
  size?: IconSize;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  dataLoc?: string;
  flip?: FlipProp;
  "aria-label"?: string;
};

const VARIANT_COLORS: Record<IconVariant, string> = {
  default: "text-gray-500",
  disabled: "text-gray-300",
  active: "text-indigo-600",
  hover: "text-gray-800",
  error: "text-red-500",
  brand: "text-indigo-500",
} as const;

const getButtonColor = (disabled: boolean): string =>
  disabled ? "text-gray-300" : "text-gray-500 hover:text-gray-800";

/**
 * Figma: https://www.figma.com/design/BO9DWO8PKNGNSI3HhpSobA/Design-System-v2?node-id=9250-9319
 */
export const Icon: React.FC<IconProps> = ({
  icon,
  size = "sm",
  variant = "default",
  dataLoc,
  spin,
  flip,
}) => {
  const { width, height } = getSize(size);

  return (
    <span className="flex justify-center p-1">
      <FontAwesomeIcon
        className={twJoin(
          "aspect-square transition-all",
          width,
          height,
          VARIANT_COLORS[variant],
        )}
        data-loc={dataLoc}
        flip={flip}
        icon={icon}
        spin={spin}
      />
    </span>
  );
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      size = "sm",
      onClick,
      disabled = false,
      dataLoc,
      flip,
      "aria-label": ariaLabel,
    },
    ref,
  ) => {
    const { width, height } = getSize(size);

    return (
      <button
        ref={ref}
        aria-label={ariaLabel}
        disabled={disabled}
        tabIndex={0}
        type="button"
        onClick={onClick}
      >
        <span
          className={twJoin(
            "flex justify-center p-1",
            disabled && "cursor-not-allowed",
          )}
        >
          <FontAwesomeIcon
            className={twJoin(
              "aspect-square transition-all",
              width,
              height,
              getButtonColor(disabled),
            )}
            data-loc={dataLoc}
            flip={flip}
            icon={icon}
          />
        </span>
      </button>
    );
  },
);
